import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import useVaultItems from '../hooks/useVaultItems';
import { Card, HealthBar, FeatureBanner, EmptyState, PillButton } from '../ui';
import { ReactComponent as ActivityIcon } from '../assets/icons/activity.svg';
import { ReactComponent as AlertTriangleIcon } from '../assets/icons/alert-triangle.svg';
import { ReactComponent as RepeatIcon } from '../assets/icons/repeat.svg';
import { ReactComponent as CalendarIcon } from '../assets/icons/calendar.svg';
import { ReactComponent as ShieldIcon } from '../assets/icons/shield.svg';
import { ReactComponent as GlobeIcon } from '../assets/icons/globe.svg';
import './Health.css';

// Un cruce por id contra la lista ligera del vault (título, usuario) para
// no tener que descifrar nada aquí: /security/health ya no expone contraseñas.
const ROW_DEFS = [
  { key: 'weak', icon: AlertTriangleIcon, tone: 'danger' },
  { key: 'reused', icon: RepeatIcon, tone: 'warn' },
  { key: 'old', icon: CalendarIcon, tone: 'warn' },
  { key: 'noTotp', icon: ShieldIcon, tone: 'accent' },
  { key: 'pwned', icon: GlobeIcon, tone: 'danger' }
];

function Health() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const rfidId = localStorage.getItem('rfidId');
  const { items } = useVaultItems({ limit: 200 });

  const [health, setHealth] = useState(null);
  const [loadingHealth, setLoadingHealth] = useState(true);
  const [checkingBreach, setCheckingBreach] = useState(false);
  const [selected, setSelected] = useState(null);

  const loadHealth = useCallback((withBreach) => {
    if (!rfidId) return;
    if (withBreach) setCheckingBreach(true); else setLoadingHealth(true);
    api.get(`/security/health/${rfidId}`, { params: withBreach ? { breach: 1 } : {} })
      .then((res) => setHealth(res.data))
      .catch(() => setHealth(null))
      .finally(() => { setLoadingHealth(false); setCheckingBreach(false); });
  }, [rfidId]);

  useEffect(() => { loadHealth(false); }, [loadHealth]);

  const itemById = useMemo(() => {
    const map = new Map();
    items.forEach((i) => map.set(String(i.id), i));
    return map;
  }, [items]);

  const rows = useMemo(() => {
    if (!health) return [];
    return ROW_DEFS.map((def) => ({ ...def, count: health.summary[def.key] ?? 0 }));
  }, [health]);

  const selectedItems = useMemo(() => {
    if (!selected || !health) return [];
    return health.items
      .filter((h) => (selected === 'pwned' ? h.pwned : h[selected]))
      .map((h) => ({ ...h, meta: itemById.get(String(h.id)) }))
      .filter((h) => h.meta);
  }, [selected, health, itemById]);

  return (
    <div className="sgc-health">
      <Card className="sgc-health__hero">
        <div>
          <p className="sgc-health__hero-label">{t('healthPage.score')}</p>
          {loadingHealth ? (
            <p className="sgc-health__loading">{t('common.loading')}</p>
          ) : (
            <HealthBar score={health?.summary?.healthScore ?? 0} />
          )}
        </div>
        <p className="sgc-health__hero-total">{t('healthPage.totalItems', { count: health?.summary?.total ?? 0 })}</p>
      </Card>

      <FeatureBanner
        icon={GlobeIcon}
        tone="accent"
        title={t('healthPage.darkWebTitle')}
        subtitle={health?.summary?.pwned !== undefined && !loadingHealth
          ? t('healthPage.pwnedCount', { count: health.summary.pwned })
          : t('healthPage.darkWebSubtitle')}
        onClick={checkingBreach ? undefined : () => loadHealth(true)}
      />
      {checkingBreach && <p className="sgc-health__checking">{t('healthPage.checkingBreach')}</p>}

      <div className="sgc-health__rows">
        {rows.map((row) => (
          <button
            key={row.key}
            type="button"
            className={`sgc-health__row ${selected === row.key ? 'sgc-health__row--active' : ''}`}
            onClick={() => setSelected(selected === row.key ? null : row.key)}
          >
            <span className={`sgc-health__row-icon sgc-health__row-icon--${row.tone}`}>
              <row.icon width={18} height={18} aria-hidden="true" />
            </span>
            <span className="sgc-health__row-label">{t(`healthPage.rows.${row.key}`)}</span>
            <span className="sgc-health__row-count">{row.count}</span>
          </button>
        ))}
      </div>

      {selected && (
        <Card padded={false} className="sgc-health__detail">
          <div className="sgc-health__detail-header">
            <h2>{t(`healthPage.rows.${selected}`)}</h2>
            <PillButton variant="secondary" size="sm" onClick={() => setSelected(null)}>{t('common.close')}</PillButton>
          </div>
          {selectedItems.length === 0 ? (
            <EmptyState icon={ActivityIcon} title={t('healthPage.emptyDetail')} />
          ) : (
            <ul className="sgc-health__detail-list">
              {selectedItems.map((h) => (
                <li key={h.id} className="sgc-health__detail-row">
                  <div>
                    <p className="sgc-health__detail-name">{h.meta.title}</p>
                    <p className="sgc-health__detail-user">{h.meta.username || '—'}</p>
                  </div>
                  <span className="sgc-health__detail-score">{h.score}</span>
                  <PillButton variant="secondary" size="sm" onClick={() => navigate(`/vault/${h.id}`)}>
                    {t('healthPage.open')}
                  </PillButton>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}
    </div>
  );
}

export default Health;
